import * as React from "react";

import { CloudApiClient } from "../lib/cloud-api-client.js";
import { CLOUD_API_BASE_URL } from "../lib/cloud-api.js";
import type { CloudSession } from "../lib/cloud-session.js";
import { clearCloudSession, loadCloudSession, storeCloudSession } from "../lib/cloud-session.js";

/** `apps/cloud-api`'s `IssuedSession` response shape (`POST /v1/auth/signin` and `POST /v1/auth/refresh`). */
interface IssuedSessionDto {
  readonly accessToken: string;
  readonly refreshToken: string;
  readonly expiresInSeconds: number;
}

function toCloudSession(issued: IssuedSessionDto): CloudSession {
  return {
    accessToken: issued.accessToken,
    refreshToken: issued.refreshToken,
    expiresAt: Date.now() + issued.expiresInSeconds * 1000,
  };
}

/**
 * A plain `fetch`, deliberately not routed through `CloudApiClient` — that
 * client's own `onUnauthorized` hook (below) exists to call this function,
 * so calling back into the client here would recurse the moment a refresh
 * attempt itself ever came back 401 (an already-reused/expired refresh
 * token, BR-AUTH-6's rotation-with-reuse-detection).
 */
async function refreshCloudSession(refreshToken: string): Promise<CloudSession> {
  const response = await fetch(`${CLOUD_API_BASE_URL}/v1/auth/refresh`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ refreshToken }),
  });
  if (!response.ok) throw new Error("refresh failed");
  const issued = (await response.json()) as IssuedSessionDto;
  return toCloudSession(issued);
}

interface CloudSessionContextValue {
  readonly session: CloudSession | null;
  readonly client: CloudApiClient;
  signIn(email: string, password: string): Promise<void>;
  signOut(): void;
}

const CloudSessionContext = React.createContext<CloudSessionContextValue | null>(null);

/**
 * §1's "sign in from any browser" — deliberately simpler than `apps/
 * desktop`'s own `CloudSessionProvider`: no local sidecar to forward a
 * token to, no periodic/proactive refresh timer. Session recovery is
 * reactive instead: a request 401ing is `CloudApiClient`'s own signal to
 * call `onUnauthorized` below, which attempts one silent refresh (the
 * `refreshToken` was already being stored and never used) and lets the
 * request quietly retry with the new token if that works. Only when the
 * refresh itself fails — a genuinely dead session, not just an expired
 * access token — does this actually sign out and hard-redirect to
 * `/sign-in`; a `window.location` navigation rather than the router's own
 * `navigate()` since this provider sits outside the router (`main.tsx`),
 * and a full reload is the simplest way to guarantee every already-mounted
 * page (mid-request, mid-render, whatever state it was in) gets torn down
 * cleanly rather than left showing stale content next to a sign-in prompt.
 */
export function CloudSessionProvider({
  children,
}: {
  children: React.ReactNode;
}): React.ReactElement {
  const [session, setSessionState] = React.useState<CloudSession | null>(() => loadCloudSession());
  const sessionRef = React.useRef(session);
  sessionRef.current = session;
  /** Concurrent 401s (several in-flight requests, one token) must share a single refresh attempt — a second `/v1/auth/refresh` call with the same (now-rotated) refresh token would itself fail BR-AUTH-6's reuse detection. */
  const refreshPromiseRef = React.useRef<Promise<boolean> | null>(null);

  const signOut = React.useCallback(() => {
    clearCloudSession();
    setSessionState(null);
  }, []);

  const onUnauthorized = React.useCallback((): Promise<boolean> => {
    if (refreshPromiseRef.current) return refreshPromiseRef.current;

    const attempt = (async (): Promise<boolean> => {
      const refreshToken = sessionRef.current?.refreshToken;
      if (!refreshToken) {
        signOut();
        window.location.href = "/sign-in";
        return false;
      }
      try {
        const next = await refreshCloudSession(refreshToken);
        storeCloudSession(next);
        setSessionState(next);
        return true;
      } catch {
        signOut();
        window.location.href = "/sign-in";
        return false;
      }
    })();

    refreshPromiseRef.current = attempt;
    return attempt.finally(() => {
      refreshPromiseRef.current = null;
    });
  }, [signOut]);

  const client = React.useMemo(
    () =>
      new CloudApiClient({
        getToken: () => sessionRef.current?.accessToken ?? null,
        onUnauthorized,
      }),
    [onUnauthorized],
  );

  const signIn = React.useCallback(
    async (email: string, password: string) => {
      const issued = await client.request<IssuedSessionDto>("POST", "/v1/auth/signin", {
        email,
        password,
      });
      const next = toCloudSession(issued);
      storeCloudSession(next);
      setSessionState(next);
    },
    [client],
  );

  const value = React.useMemo<CloudSessionContextValue>(
    () => ({ session, client, signIn, signOut }),
    [session, client, signIn, signOut],
  );

  return <CloudSessionContext.Provider value={value}>{children}</CloudSessionContext.Provider>;
}

export function useCloudSession(): CloudSessionContextValue {
  const ctx = React.useContext(CloudSessionContext);
  if (!ctx) throw new Error("useCloudSession must be used within a <CloudSessionProvider>");
  return ctx;
}
